/**
 * Modal para correção de discrepâncias de limites
 * Permite selecionar membros/inboxes para desativar
 * 
 * Versão: 1.0.0
 * Data: 2026-01-08
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Users, Inbox, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { TenantLimitAudit } from '@/hooks/useAdminTenantLimitsAudit';
import { MemberSelectionList } from './MemberSelectionList';
import { InboxSelectionList } from './InboxSelectionList';
import { 
  useDeactivateMembers, 
  useSuspendInboxes 
} from '@/hooks/useAdminTenantLimitsAudit';

interface DiscrepancyFixModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenant: TenantLimitAudit | null;
  onSuccess: () => void;
}

/**
 * Modal principal para correção de discrepâncias
 */
export function DiscrepancyFixModal({
  open,
  onOpenChange,
  tenant,
  onSuccess,
}: DiscrepancyFixModalProps) {
  const { t } = useTranslation();
  
  // Estados de seleção
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [selectedInboxes, setSelectedInboxes] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'members' | 'inboxes'>('members');
  
  // Mutations
  const deactivateMembers = useDeactivateMembers();
  const suspendInboxes = useSuspendInboxes();
  
  // Resetar seleções quando modal fecha
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedMembers([]);
      setSelectedInboxes([]);
      setActiveTab('members');
    }
    onOpenChange(isOpen);
  };
  
  if (!tenant) return null;
  
  const hasMemberDiscrepancy = tenant.members_over_limit > 0;
  const hasInboxDiscrepancy = tenant.inboxes_over_limit > 0;
  
  // Validar se seleção está correta para membros
  const isMemberSelectionValid = selectedMembers.length === tenant.members_over_limit;
  const isInboxSelectionValid = selectedInboxes.length === tenant.inboxes_over_limit;
  
  // Verificar se está processando
  const isProcessing = deactivateMembers.isPending || suspendInboxes.isPending;
  
  // Desativar membros selecionados
  const handleDeactivateMembers = async () => {
    if (!isMemberSelectionValid) return;
    
    try {
      await deactivateMembers.mutateAsync({
        memberIds: selectedMembers,
        tenantId: tenant.tenant_id,
      });
      setSelectedMembers([]);
      onSuccess();
      
      // Se não há mais discrepância de inbox, fechar modal
      if (!hasInboxDiscrepancy) {
        handleOpenChange(false);
      }
    } catch (error) {
      // Erro já tratado pelo hook
    }
  };
  
  // Suspender inboxes selecionadas
  const handleSuspendInboxes = async () => {
    if (!isInboxSelectionValid) return;
    
    try {
      await suspendInboxes.mutateAsync({
        inboxIds: selectedInboxes,
        tenantId: tenant.tenant_id,
      });
      setSelectedInboxes([]);
      onSuccess();
      
      // Se não há mais discrepância de membros, fechar modal
      if (!hasMemberDiscrepancy) {
        handleOpenChange(false);
      }
    } catch (error) {
      // Erro já tratado pelo hook
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Corrigir Discrepância
          </DialogTitle>
          <DialogDescription>
            Selecione os recursos excedentes para desativar no tenant{' '}
            <strong>{tenant.tenant_name}</strong>
          </DialogDescription>
        </DialogHeader>
        
        {/* Resumo da discrepância */}
        <div className="flex gap-4 py-2">
          {hasMemberDiscrepancy && (
            <Badge variant="destructive" className="gap-1">
              <Users className="h-3 w-3" />
              {tenant.members_over_limit} membro(s) excedente(s)
            </Badge>
          )}
          {hasInboxDiscrepancy && (
            <Badge variant="destructive" className="gap-1">
              <Inbox className="h-3 w-3" />
              {tenant.inboxes_over_limit} inbox(es) excedente(s)
            </Badge>
          )}
        </div>
        
        {/* Abas de seleção */}
        <Tabs 
          value={activeTab} 
          onValueChange={(v) => setActiveTab(v as 'members' | 'inboxes')}
          className="flex-1 overflow-hidden flex flex-col"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger 
              value="members" 
              disabled={!hasMemberDiscrepancy}
              className="gap-1"
            >
              <Users className="h-4 w-4" />
              Membros
              {hasMemberDiscrepancy && (
                <Badge variant="secondary" className="ml-1 text-xs">
                  {selectedMembers.length}/{tenant.members_over_limit}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="inboxes" 
              disabled={!hasInboxDiscrepancy}
              className="gap-1"
            >
              <Inbox className="h-4 w-4" />
              Inboxes
              {hasInboxDiscrepancy && (
                <Badge variant="secondary" className="ml-1 text-xs">
                  {selectedInboxes.length}/{tenant.inboxes_over_limit}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
          
          {/* Conteúdo das abas */}
          <TabsContent 
            value="members" 
            className="flex-1 overflow-auto mt-4"
          >
            {hasMemberDiscrepancy ? (
              <MemberSelectionList
                tenantId={tenant.tenant_id}
                requiredCount={tenant.members_over_limit}
                selectedIds={selectedMembers}
                onSelectionChange={setSelectedMembers}
              />
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                Nenhuma discrepância de membros
              </div>
            )}
          </TabsContent>
          
          <TabsContent 
            value="inboxes" 
            className="flex-1 overflow-auto mt-4"
          >
            {hasInboxDiscrepancy ? (
              <InboxSelectionList
                tenantId={tenant.tenant_id}
                requiredCount={tenant.inboxes_over_limit}
                selectedIds={selectedInboxes}
                onSelectionChange={setSelectedInboxes}
              />
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                Nenhuma discrepância de inboxes
              </div>
            )}
          </TabsContent>
        </Tabs>
        
        <DialogFooter className="mt-4 border-t pt-4">
          <Button 
            variant="outline" 
            onClick={() => handleOpenChange(false)}
            disabled={isProcessing}
          >
            Cancelar
          </Button>
          
          {activeTab === 'members' && hasMemberDiscrepancy && (
            <Button
              variant="destructive"
              onClick={handleDeactivateMembers}
              disabled={!isMemberSelectionValid || isProcessing}
            >
              {deactivateMembers.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Desativando...
                </>
              ) : (
                <>
                  Desativar {selectedMembers.length} Membro(s)
                </>
              )}
            </Button>
          )}
          
          {activeTab === 'inboxes' && hasInboxDiscrepancy && (
            <Button
              variant="destructive"
              onClick={handleSuspendInboxes}
              disabled={!isInboxSelectionValid || isProcessing}
            >
              {suspendInboxes.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Suspendendo...
                </>
              ) : (
                <>
                  Suspender {selectedInboxes.length} Inbox(es)
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
